import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiAward,
  FiBookOpen,
  FiBriefcase,
  FiCheckCircle,
  FiClock,
  FiHeart,
  FiLayers,
  FiMessageCircle,
  FiMonitor,
  FiShield,
  FiTarget,
  FiTrendingUp,
  FiUsers,
  FiZap,
  FiArrowRight,
} from "react-icons/fi";
import Layout from "../../components/Layout";
import TrainingHero from "./TrainingHero";
import ContactSection4 from "../contact/ContactSection4";

const SITE_URL = "https://mbasolutions.co";

const CORPORATE_SEO = {
  title:
    "Corporate Training Courses | Leadership, Compliance & Soft Skills | MBA Solutions",
  description:
    "MBA Solutions corporate training courses for leadership, compliance, safety, soft skills, and technical capability. Customized in-house, online, and blended programs for teams across Pakistan.",
  keywords:
    "corporate training Pakistan, leadership training, compliance training, soft skills training, workforce development, in-house corporate training, MBA Solutions corporate training",
  canonical: `${SITE_URL}/corporate-training`,
};

const whyChoose = [
  {
    title: "Learning Partner",
    desc: "We act as your learning ally — focused on practical capability that supports real business growth, not one-off workshops.",
    icon: FiUsers,
  },
  {
    title: "Certified Experts",
    desc: "Sessions are delivered by experienced trainers and practitioners with hands-on industry and compliance backgrounds.",
    icon: FiAward,
  },
  {
    title: "Trusted Industry Name",
    desc: "Organizations across manufacturing, services, food, IT, and healthcare trust MBA Solutions for dependable training delivery.",
    icon: FiBriefcase,
  },
  {
    title: "Result-Driven Programs",
    desc: "Every program is designed around measurable outcomes — clearer processes, stronger teams, and audit-ready competence.",
    icon: FiTarget,
  },
  {
    title: "Flexible & Modern Delivery",
    desc: "Choose in-person, online, or blended formats so learning fits your schedule, sites, and workforce model.",
    icon: FiMonitor,
  },
  {
    title: "Customized Training",
    desc: "We tailor content, case studies, and exercises to your industry, culture, and strategic priorities.",
    icon: FiLayers,
  },
];

const processSteps = [
  {
    step: "01",
    title: "Discover Needs",
    desc: "We clarify your goals, audience, risks, and performance gaps before recommending a pathway.",
  },
  {
    step: "02",
    title: "Design the Program",
    desc: "Content, case studies, and assessments are tailored to your industry and workplace context.",
  },
  {
    step: "03",
    title: "Deliver & Engage",
    desc: "Expert facilitators run interactive sessions on-site, online, or in a blended format.",
  },
  {
    step: "04",
    title: "Measure Impact",
    desc: "Feedback, assessments, and practical follow-through help you track learning ROI.",
  },
];

const courseAreas = [
  {
    title: "Compliance Training",
    desc: "Help teams understand legal and regulatory expectations, reduce risk, and build a culture of accountability.",
    icon: FiShield,
  },
  {
    title: "Leadership Training",
    desc: "Prepare supervisors and emerging leaders with practical tools to guide teams and drive performance.",
    icon: FiTrendingUp,
  },
  {
    title: "Soft Skills Training",
    desc: "Strengthen communication, teamwork, problem-solving, and emotional intelligence across roles.",
    icon: FiMessageCircle,
  },
  {
    title: "Safety Courses",
    desc: "Practical workplace safety learning aligned with operational risks and regulatory expectations.",
    icon: FiShield,
    link: "/safety-training",
  },
  {
    title: "Quality, Health & Safety",
    desc: "Programs that reinforce ISO-aligned QHSE practices and safer, more consistent operations.",
    icon: FiAward,
    link: "/highfield-training",
  },
  {
    title: "Technical Training",
    desc: "Role-specific technical programs that deepen job competence and reduce operational errors.",
    icon: FiZap,
  },
  {
    title: "Product Training",
    desc: "Equip employees to explain, support, and deliver your products and services with confidence.",
    icon: FiBookOpen,
  },
  {
    title: "Technology Training",
    desc: "Keep teams current with systems, digital workflows, and tools that shape modern operations.",
    icon: FiMonitor,
  },
  {
    title: "Onboarding Training",
    desc: "Accelerate new-hire ramp-up with structured orientation covering culture, processes, and role essentials.",
    icon: FiUsers,
  },
  {
    title: "Human Resource Training",
    desc: "Support HR and people managers with practical programs on policy, engagement, and workforce capability.",
    icon: FiBriefcase,
  },
  {
    title: "Sales Training",
    desc: "Build consultative selling skills, objection handling, and customer-focused performance.",
    icon: FiTrendingUp,
  },
  {
    title: "Customer Service Excellence",
    desc: "Teach proactive, professional service behaviors that improve satisfaction and loyalty.",
    icon: FiHeart,
  },
  {
    title: "Communication Skills",
    desc: "Improve business writing, presentations, negotiation, and cross-team collaboration.",
    icon: FiMessageCircle,
  },
  {
    title: "Coaching Programs",
    desc: "Targeted coaching that helps managers and specialists convert learning into day-to-day habits.",
    icon: FiTarget,
  },
  {
    title: "DEI Training",
    desc: "Foster workplaces where people feel respected, included, and able to contribute fully.",
    icon: FiUsers,
  },
  {
    title: "Health & Wellbeing",
    desc: "Support mental health awareness, ergonomics, crisis readiness, and workplace wellness.",
    icon: FiHeart,
  },
  {
    title: "Time Management",
    desc: "Help professionals prioritize work, reduce overwhelm, and improve personal productivity.",
    icon: FiClock,
  },
  {
    title: "Interactive Training",
    desc: "Engaging sessions using discussions, workshops, and applied exercises for stronger retention.",
    icon: FiLayers,
  },
  {
    title: "Role-Play Workshops",
    desc: "Practice real scenarios so teams build confidence before applying skills on the job.",
    icon: FiUsers,
  },
  {
    title: "Instructor-Led Training",
    desc: "Expert-facilitated classroom or virtual sessions for high-engagement learning.",
    icon: FiBookOpen,
  },
  {
    title: "Skills Education",
    desc: "Structured pathways that develop technical and professional skills for long-term growth.",
    icon: FiLayers,
  },
  {
    title: "Simulation Training",
    desc: "Learn by doing through simulations that mirror real operational decisions and pressures.",
    icon: FiZap,
  },
  {
    title: "Orientation Courses",
    desc: "Help new employees feel confident, connected, and productive from day one.",
    icon: FiBriefcase,
  },
  {
    title: "eLearning Courses",
    desc: "Scalable digital modules for consistent learning across locations and shifts.",
    icon: FiMonitor,
  },
  {
    title: "Blended Learning",
    desc: "Combine live facilitation with digital content for flexibility without losing impact.",
    icon: FiLayers,
  },
  {
    title: "On-the-Job Training",
    desc: "Practical coaching at the workstation so skills transfer immediately into performance.",
    icon: FiCheckCircle,
  },
];

const INITIAL_COURSE_COUNT = 12;

const uniquePoints = [
  "Customized programs tailored to your business needs and industry context",
  "Flexible delivery — in-person, online, or hybrid",
  "Modern learning methods including simulations, role plays, and case studies",
  "Instruction from experienced practitioners and certified trainers",
  "Clear focus on measurable improvement and training ROI",
];

const audiences = [
  "Entry-level employees",
  "Middle managers",
  "Team leaders and supervisors",
  "Senior executives",
  "Human resource departments",
];

const relatedCourses = [
  { name: "IOSH Managing Safely", link: "/iosh-managing-safely-training" },
  { name: "Safety Training Courses", link: "/safety-training" },
  { name: "Highfield (HABC) Courses", link: "/highfield-training" },
  { name: "ISO Lead Auditor Course", link: "/iso-lead-auditor-training" },
  { name: "HACCP Level 2 / 3 / 4", link: "/haccp-training" },
  { name: "PECB Training", link: "/pecb-training" },
  { name: "All Training Programs", link: "/training" },
];

const faqs = [
  {
    q: "What are corporate training programs, and how do they help organizations?",
    a: "Corporate training programs are structured learning initiatives that strengthen employee skills, improve productivity, and align workforce capability with your business goals and compliance requirements.",
  },
  {
    q: "Which corporate training programs work best for employee development?",
    a: "High-impact areas typically include leadership, communication, compliance, quality & safety, customer service, and role-specific technical skills — selected based on your team’s gaps and priorities.",
  },
  {
    q: "Can MBA Solutions customize training for our industry?",
    a: "Yes. We adapt content, examples, and delivery methods to your industry, company culture, and operational objectives so learning is relevant and actionable.",
  },
  {
    q: "What delivery formats do you offer?",
    a: "We deliver on-site, virtual, and hybrid programs so you can train teams across locations with minimal disruption to daily operations.",
  },
  {
    q: "How do we measure training success?",
    a: "Success can be tracked through pre/post assessments, participant feedback, manager observations, and performance indicators linked to your organizational goals.",
  },
  {
    q: "How do we start a corporate training engagement?",
    a: "Share your goals through the Contact for Training Courses form. Our team will recommend a suitable program outline, delivery format, and schedule for your workforce.",
  },
];

const buildCorporateJsonLd = () => [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: CORPORATE_SEO.title,
    description: CORPORATE_SEO.description,
    url: CORPORATE_SEO.canonical,
    isPartOf: {
      "@type": "WebSite",
      name: "MBA Solutions",
      url: SITE_URL,
    },
    about: {
      "@type": "Service",
      name: "Corporate Training Courses",
      provider: {
        "@type": "Organization",
        name: "MBA Solutions",
        url: SITE_URL,
      },
      serviceType: "Corporate training and workforce development",
      areaServed: "Pakistan",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: SITE_URL,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Training",
        item: `${SITE_URL}/training`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Corporate Training",
        item: CORPORATE_SEO.canonical,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Corporate Training Course Areas",
    numberOfItems: courseAreas.length,
    itemListElement: courseAreas.map((course, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: course.title,
      description: course.desc,
      ...(course.link
        ? { url: `${SITE_URL}${course.link}` }
        : { url: CORPORATE_SEO.canonical }),
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  },
];

const SectionHeading = ({ title, subtitle, light = false }) => (
  <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
    <h2
      className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-4 ${
        light ? "text-white" : "text-gray-900"
      }`}
    >
      {title}
    </h2>
    <div
      className={`w-20 h-1 mx-auto rounded-full mb-5 ${
        "bg-[#F86624]"
      }`}
      aria-hidden="true"
    />
    {subtitle && (
      <p
        className={`text-base md:text-lg leading-relaxed ${
          light ? "text-white/85" : "text-gray-600"
        }`}
      >
        {subtitle}
      </p>
    )}
  </div>
);

const CorporateTraining = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [showAllCourses, setShowAllCourses] = useState(false);

  const visibleCourses = showAllCourses
    ? courseAreas
    : courseAreas.slice(0, INITIAL_COURSE_COUNT);

  return (
    <Layout
      title={CORPORATE_SEO.title}
      description={CORPORATE_SEO.description}
      keywords={CORPORATE_SEO.keywords}
      canonical={CORPORATE_SEO.canonical}
      jsonLd={buildCorporateJsonLd()}
    >
      <TrainingHero
        badge="Workforce Development"
        title="Corporate Training Courses"
        subtitle="Customized corporate training programs designed to strengthen leadership, compliance, soft skills, and technical capability — delivered in-house, online, or blended."
        icon={FiUsers}
        primaryCta={{ label: "Request Training", to: "#training-contact" }}
        secondaryCta={{ label: "All Training", to: "/training" }}
      />

      {/* Intro — SEO-rich overview */}
      <section className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <p className="text-sm font-semibold uppercase tracking-wide text-[#F86624] mb-3">
              In-House &amp; Custom Programs
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">
              Build a Capable, Compliant, and High-Performing Workforce
            </h2>
            <div
              className="w-16 h-1 bg-[#F86624] rounded-full mb-6"
              aria-hidden="true"
            />
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-4">
              MBA Solutions delivers corporate training courses that help
              organizations upskill teams, strengthen compliance culture, and
              improve day-to-day performance. From leadership and soft skills to
              safety, quality, and technical competence — programs are tailored
              to your industry and operational goals.
            </p>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-6">
              Whether you need a one-day workshop or a multi-module learning
              pathway, our trainers combine practical facilitation with
              real-world case studies so learning transfers back to the
              workplace.
            </p>
            <a
              href="#corporate-courses"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--bg-primary)] hover:text-[#F86624] transition-colors"
            >
              Browse course areas
              <FiArrowRight size={16} aria-hidden="true" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-5 bg-gray-50 rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm"
          >
            <h3 className="text-lg font-bold text-gray-900 mb-5">
              Delivery Options
            </h3>
            <ul className="space-y-4 list-none">
              {[
                "On-site / in-house sessions at your facility",
                "Live online training for multi-site teams",
                "Blended learning pathways",
                "Customized industry programs",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <FiCheckCircle
                    size={18}
                    className="text-[var(--bg-primary)] mt-0.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-gray-700 text-sm md:text-base">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-gray-50 py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="How Our Corporate Training Works"
            subtitle="A clear process from needs analysis to measurable learning outcomes."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {processSteps.map((item, index) => (
              <motion.article
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm h-full"
              >
                <span className="text-sm font-bold text-[#F86624]">
                  {item.step}
                </span>
                <h3 className="text-lg font-bold text-gray-900 mt-2 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.desc}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="Why Choose MBA Solutions as Your Corporate Training Provider?"
            subtitle="Unlock the potential of your workforce with expertly designed programs tailored to real business challenges."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {whyChoose.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.05 }}
                  className="group bg-gray-50 hover:bg-white border border-gray-100 rounded-2xl p-6 md:p-7 shadow-sm hover:shadow-xl transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-[var(--bg-primary)]/10 text-[var(--bg-primary)] flex items-center justify-center mb-5 group-hover:bg-[var(--bg-primary)] group-hover:text-white transition-colors">
                    <Icon size={22} aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[var(--bg-primary)] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Course range */}
      <section
        id="corporate-courses"
        className="bg-gray-50 py-14 md:py-20 px-4 sm:px-6 lg:px-8 scroll-mt-28"
      >
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="A Complete Range of Corporate Training Courses"
            subtitle="Browse workplace programs covering compliance, leadership, technical skills, soft skills, and flexible learning formats."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
            {visibleCourses.map((course, index) => {
              const Icon = course.icon;
              return (
                <motion.article
                  key={course.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (index % 8) * 0.03 }}
                  whileHover={{ y: -6 }}
                  className="group bg-white rounded-2xl border border-gray-100 p-5 md:p-6 shadow-sm hover:shadow-lg hover:border-[var(--bg-primary)]/20 transition-all h-full flex flex-col"
                >
                  <div className="w-10 h-10 rounded-lg bg-[var(--bg-primary)]/10 text-[var(--bg-primary)] flex items-center justify-center mb-4 group-hover:bg-[var(--bg-primary)] group-hover:text-white transition-colors">
                    <Icon size={18} aria-hidden="true" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">
                    {course.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-1 mb-4">
                    {course.desc}
                  </p>
                  {course.link ? (
                    <Link
                      to={course.link}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--bg-primary)] hover:text-[#F86624] transition-colors"
                    >
                      View programs
                      <FiArrowRight size={14} aria-hidden="true" />
                    </Link>
                  ) : (
                    <a
                      href="#training-contact"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--bg-primary)] hover:text-[#F86624] transition-colors"
                    >
                      Enquire
                      <FiArrowRight size={14} aria-hidden="true" />
                    </a>
                  )}
                </motion.article>
              );
            })}
          </div>

          {courseAreas.length > INITIAL_COURSE_COUNT && (
            <div className="flex justify-center mt-10">
              <button
                type="button"
                onClick={() => setShowAllCourses((prev) => !prev)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--bg-primary)] hover:opacity-90 text-white text-sm font-semibold shadow-md transition-opacity"
              >
                {showAllCourses
                  ? "Show fewer course areas"
                  : `Show all ${courseAreas.length} course areas`}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Certified trainers */}
      <section className="bg-white py-14 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--bg-primary)]/10 text-[var(--bg-primary)] mb-5">
              <FiAward size={28} aria-hidden="true" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Certified Corporate Trainers
            </h2>
            <div className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-5" aria-hidden="true" />
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              Our facilitators bring real-world consulting and industry
              experience into every session — so your teams learn from people
              who have implemented the systems they teach. Whether you need a
              one-time workshop or long-term capability building, we help you
              develop internal strength that lasts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Unique + audience */}
      <section className="bg-gray-50 py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl border border-gray-100 p-7 md:p-8 shadow-sm"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
              What Makes Our Corporate Training Solutions Unique?
            </h2>
            <div className="w-16 h-1 bg-[#F86624] rounded-full mb-5" aria-hidden="true" />
            <ul className="space-y-3 list-none">
              {uniquePoints.map((point) => (
                <li key={point} className="flex items-start gap-3 text-gray-700">
                  <FiCheckCircle
                    size={18}
                    className="text-[var(--bg-primary)] mt-0.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm md:text-base leading-relaxed">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl border border-gray-100 p-7 md:p-8 shadow-sm"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
              Corporate Learning &amp; Development That Grows With You
            </h2>
            <div className="w-16 h-1 bg-[#F86624] rounded-full mb-5" aria-hidden="true" />
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-6">
              Learning never stops — and neither should capability building.
              With ongoing corporate learning and development support, your
              teams stay ready for new technology, stronger customer
              expectations, and evolving compliance requirements.
            </p>
            <h3 className="text-base font-bold text-gray-900 mb-3">
              Who Can Benefit?
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 list-none">
              {audiences.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[#F86624]"
                    aria-hidden="true"
                  />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-500 mt-5">
              From one-time workshops to long-term professional development
              programs, we cover the full learning journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Related courses */}
      <section className="bg-white py-14 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="Related Training Programs"
            subtitle="Explore complementary courses that pair well with corporate capability building."
          />
          <div className="flex flex-wrap justify-center gap-3">
            {relatedCourses.map((course) => (
              <Link
                key={course.name}
                to={course.link}
                className="inline-flex items-center px-4 py-2.5 rounded-full border border-gray-200 bg-gray-50 text-sm font-medium text-gray-800 hover:border-[var(--bg-primary)]/40 hover:text-[var(--bg-primary)] hover:bg-white transition-colors"
              >
                {course.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section
        className="bg-gray-50 py-14 md:py-20 px-4 sm:px-6 lg:px-8"
        aria-label="Corporate training frequently asked questions"
        id="corporate-faq"
      >
        <div className="max-w-3xl mx-auto">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="space-y-3">
            {faqs.map((faq, index) => {
              const open = openFaq === index;
              return (
                <div
                  key={faq.q}
                  className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? null : index)}
                    className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 md:px-6 md:py-5"
                    aria-expanded={open}
                  >
                    <span className="font-semibold text-gray-900 text-sm md:text-base">
                      {faq.q}
                    </span>
                    <motion.span
                      animate={{ rotate: open ? 180 : 0 }}
                      className="text-[var(--bg-primary)] flex-shrink-0"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <p className="px-5 pb-5 md:px-6 text-sm text-gray-600 leading-relaxed border-t border-gray-50 pt-3">
                          {faq.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section
        className="bg-white pt-10 md:pt-14"
        aria-label="Contact for training courses"
        id="training-contact"
      >
        <div className="max-w-3xl mx-auto px-4 text-center mb-2">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Contact for Training Courses
          </h2>
          <div
            className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-4"
            aria-hidden="true"
          />
          <p className="text-gray-600">
            Tell us about your team, goals, and preferred delivery format — our
            training specialists will recommend a practical program outline.
          </p>
        </div>
        <ContactSection4 />
      </section>

      {/* Closing CTA */}
      <section className="relative overflow-hidden py-14 md:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1a1a2e] via-[#3d1f5c] to-[var(--bg-primary)] text-white">
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Upgrade Your Team&apos;s Potential?
          </h2>
          <div className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-5" aria-hidden="true" />
          <p className="text-white/85 mb-8 leading-relaxed">
            Partner with MBA Solutions for results-driven corporate training
            that builds capability, compliance, and lasting performance.
          </p>
          <a
            href="#training-contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#F86624] hover:bg-[#e55a1a] text-white font-semibold shadow-lg transition-all"
          >
            Request Corporate Training
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default CorporateTraining;
